/**
 * Telling somebody what happened, and holding them while it happens.
 *
 * The application says these things in a dozen registers today: a toast
 * library at its defaults, a hundred separate spinners, a full-screen overlay
 * with gradient text and spinning sparkles, and a solid red bar for a failed
 * payment. Everything here says the same things in one voice.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { AlertTriangle, Check, Info, RefreshCw, Sparkles, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Button } from './primitives'
import './feedback.css'

/* ---------------- Waiting ---------------- */

/**
 * One ring, turning: the same ring a button draws while it works, so there is
 * one spinner in the product rather than two that nearly match. It stops
 * turning under reduced motion.
 */
export function Spinner({
  lg,
  className,
  quiet,
}: {
  lg?: boolean
  className?: string
  /**
   * For a spinner that already sits inside a live region.
   *
   * Announcing "Working" from inside a `role="status"` panel gives a screen
   * reader two live regions for one event, and gives `getByRole('status')`
   * two answers. Inside such a panel the spinner is decoration: the panel
   * says what is happening.
   */
  quiet?: boolean
}) {
  return (
    <span
      className={cn('ds-spin', lg && 'ds-spin--lg', className)}
      role={quiet ? undefined : 'status'}
      aria-label={quiet ? undefined : 'Working'}
      aria-hidden={quiet || undefined}
    />
  )
}

/**
 * How far along a piece of work is, when the number is real.
 *
 * A plain ink fill on a pressed-paper track, the same shape the readiness
 * bar and the meter use. It is not a spinner with a number bolted on: give
 * it a percentage only where one can be honestly worked out, and give it a
 * label saying what is being measured, because a bar on its own says
 * "something is happening" and every screen already has a way to say that.
 */
export function Progress({ value, label }: { value: number; label: string }) {
  const pct = Math.max(0, Math.min(100, value))
  return (
    <div
      className="ds-progress"
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <span className="ds-progress__fill" style={{ width: `${pct}%` }} />
    </div>
  )
}

/* ---------------- The toast ---------------- */

export type ToastKind = 'done' | 'look' | 'working'

export function Toast({
  kind = 'done',
  title,
  detail,
  action,
  onDismiss,
}: {
  kind?: ToastKind
  title: string
  detail?: string
  action?: ReactNode
  onDismiss?: () => void
}) {
  return (
    <div className="ds-toast" data-kind={kind} role="status" aria-live="polite">
      <span className="ds-toast__mark" aria-hidden="true">
        {kind === 'done' && <Check />}
        {kind === 'look' && <AlertTriangle />}
        {kind === 'working' && <Spinner />}
      </span>
      <div className="ds-toast__body">
        <div className="ds-toast__title">{title}</div>
        {detail && <p className="ds-toast__detail">{detail}</p>}
        {action && <div className="ds-toast__foot">{action}</div>}
      </div>
      {onDismiss && (
        <button type="button" className="ds-toast__close" onClick={onDismiss} aria-label="Dismiss">
          <X aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

export function ToastStack({ children }: { children: ReactNode }) {
  return <div className="ds-toasts">{children}</div>
}

/* ---------------- While the product works ---------------- */

export interface BusyStep {
  label: string
  state: 'done' | 'doing' | 'waiting'
}

/**
 * What is happening, and what is already finished. It sits inside the surface
 * it belongs to rather than covering the whole application, so a person can
 * still see what they were looking at.
 */
export function Working({ title, note, steps }: { title: string; note?: string; steps?: BusyStep[] }) {
  return (
    <div className="ds-busy">
      <div className="ds-busy__card" role="status" aria-live="polite">
        <div className="ds-busy__head">
          <Spinner lg quiet />
          <div className="ds-busy__title">{title}</div>
        </div>
        {note && <p className="ds-busy__note">{note}</p>}
        {steps && (
          <ul className="ds-busy__steps">
            {steps.map((s) => (
              <li key={s.label} className="ds-busy__step" data-state={s.state}>
                <span className="ds-busy__tick" aria-hidden="true">
                  {s.state === 'done' ? <Check /> : s.state === 'doing' ? <Spinner quiet /> : null}
                </span>
                {s.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

/* ---------------- A bar across the top ---------------- */

/**
 * An account-wide fact, above everything, where it cannot be scrolled past.
 * Even the urgent one keeps a paper background: a solid red bar across the
 * product is a fill, and fills belong to the moment somebody confirms
 * something, not to a standing condition.
 */
export function TopBanner({
  kind = 'look',
  children,
  action,
}: {
  /**
   * `good` is for news somebody is pleased to get. It follows the same rule
   * as the other three: paper, a coloured edge, coloured words.
   *
   * `loud` is the one exception, and it is deliberately narrow: a single
   * announcement a person should not miss, which they can dismiss for good.
   * It is a fill in the accent, because that is the one thing on the page
   * nothing else competes with. It earns the fill by going away when it is
   * clicked; a bar that came back every session would not.
   */
  kind?: 'look' | 'urgent' | 'quiet' | 'good' | 'loud'
  children: ReactNode
  action?: ReactNode
}) {
  const Icon = kind === 'quiet' ? Info : kind === 'good' || kind === 'loud' ? Sparkles : AlertTriangle
  return (
    <div className="ds-topbar" data-kind={kind}>
      <Icon className="ds-topbar__icon" aria-hidden="true" />
      <div className="ds-topbar__text">{children}</div>
      {action}
    </div>
  )
}

/* ---------------- The moment something lands ---------------- */

/** Names what happened and what it means. No confetti. */
export function Moment({ title, detail, children }: { title: string; detail?: string; children?: ReactNode }) {
  return (
    <div className="ds-moment">
      <div className="ds-moment__mark" aria-hidden="true">
        <Check />
      </div>
      <div className="ds-moment__title">{title}</div>
      {detail && <p className="ds-moment__detail">{detail}</p>}
      {children && <div className="ds-moment__foot">{children}</div>}
    </div>
  )
}

/* ---------------- When it did not work ---------------- */

/**
 * What went wrong in words a person can act on, what to do about it, and a
 * reference they can quote if they get in touch. Never a stack trace, never
 * "an error occurred".
 */
export function Broke({
  title,
  detail,
  onRetry,
  retryLabel = 'Try again',
  secondary,
  reference,
}: {
  title: string
  detail: string
  onRetry?: () => void
  /** The primary button. "Try again" unless the action is something else. */
  retryLabel?: string
  secondary?: ReactNode
  reference?: string
}) {
  return (
    <div className="ds-broke" role="alert">
      <div className="ds-broke__mark" aria-hidden="true">
        <AlertTriangle />
      </div>
      <div className="ds-broke__title">{title}</div>
      <p className="ds-broke__detail">{detail}</p>
      <div className="ds-broke__foot">
        {onRetry && (
          <Button onClick={onRetry}>
            <RefreshCw aria-hidden="true" />
            {retryLabel}
          </Button>
        )}
        {secondary}
      </div>
      {reference && <p className="ds-broke__ref">If you get in touch, quote {reference}.</p>}
    </div>
  )
}

/* ---------------- A demo helper for the kit ---------------- */

/** Runs the busy card through its steps so the kit shows it working. */
export function useDemoSteps(labels: string[], ms = 1100) {
  const [i, setI] = useState(0)
  useEffect(() => {
    const t = window.setTimeout(() => setI((n) => (n + 1) % (labels.length + 1)), ms)
    return () => window.clearTimeout(t)
  }, [i, labels.length, ms])
  return labels.map((label, n): BusyStep => ({ label, state: n < i ? 'done' : n === i ? 'doing' : 'waiting' }))
}
